import { dia, shapes } from '@joint/plus';
import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ShapeTypesEnum } from '../../../joint-plus/shapes/app.shapes';
import { State } from '../../../redux/reducer';
import './Inspector.scss';
import LabelInspector from './LabelInspector';
import LinkInspector from './LinkInspector';
import MessageInspector from './MessageInspector';

import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { useIntl } from 'react-intl';
import CustomizedSnackbars from 'src/components/material-snackbars';
import { Overlay } from 'src/styles/global';

import * as ChatbotFlowService from "../../../../../../../../services/2talk/chatbot-flow.service.js";
import AvaliationInspector from './AvaliationInspector';
import CreateTicketInspector from './CreateTicketInspector';
import FlowInspector from './FlowInspector';
import ResponseOrientationInspector from './ResponseOrientationInspector';
import ResponseParamInspector from './ResponseParamInspector';
import ServiceInspector from './ServiceInspector';
import TalkAttendantInspector from './TalkAttendantInspector';
import LLMInspector from './LLMInspector';
import ConditionInspector from './ConditionInspector';
import ServicePropertiesPanel from '../PropertyPanel/ServicePropertiesPanel';
import { useServicePanelMode } from '../PropertyPanel/useServicePanelMode';

/**
 * Inspector — v2 (F5.4)
 *
 * Comportamento novo pra caixa `app.Service`:
 *  - Renderiza com DUAS ABAS: "Dados Gerais" (cell — description/displayText) e
 *    "Propriedades do Serviço" (integration-service — headers/params/body).
 *  - A aba "Propriedades" pode ser DESACOPLADA — vira painel flutuante (Postman).
 *    Nesse modo, o slot da aba mostra placeholder + botao pra acoplar de volta.
 *  - Preferencia persiste em localStorage (`mexx.chatbot.service-panel.mode`).
 *
 * Pras outras caixas, comportamento inalterado (renderiza inspector unico).
 */

const Inspector = ({chatbotId, chatbotFlowId, json} : {chatbotFlowId: string, chatbotId: string, json : any}): ReactElement => {
    const intl = useIntl();

    const [cell, setCell] = useState<dia.Cell>(null);
    const selection = useSelector((state: State) => state.selection);

    const [statePage, setStatePage] = useState({
        loading: false,
        openNotification: false,
        notificationVariant: "error",
        notificationMessage: ""
    });

    // Aba ativa quando a cell eh Service (0 = Dados Gerais, 1 = Propriedades).
    const [serviceTab, setServiceTab] = useState<number>(0);

    // Modo do panel de service — hook compartilhado sincroniza com Chatbot.tsx.
    const [servicePanelMode, toggleServicePanelMode] = useServicePanelMode();

    const closeNotification = () => {
        setStatePage(prevState => ({...prevState, openNotification: false}));
    };

    const saveJson = async (json: string | null | any) => {
        setStatePage(prevState => ({...prevState, loading: true}));
        try {
            const input = { id: chatbotFlowId, jsonflow: json };
            const output = await ChatbotFlowService.addJsonFlow(input);
            if (!output?.success) {
                throw new Error("Não foi possível salvar o fluxo");
            }
            setStatePage({
                loading: false,
                openNotification: true,
                notificationVariant: "success",
                notificationMessage: "Fluxo salvo com sucesso"
            });
        } catch (err) {
            setStatePage({
                loading: false,
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: "Não foi possível salvar o fluxo"
            });
        } finally {
            setStatePage(prevState => ({...prevState, loading: false}));
        }
    }

    const handleSubmitSaveJson = async () => {
        if (!json || json?.cells?.length <= 0) {
            json = null
        } else {
            json = JSON.stringify(json);
        }
        await saveJson(json);
    }

    // Callbacks pro ServicePropertiesPanel (docked).
    const currentServiceId: number | null = (() => {
        if (!cell || cell.get('type') !== ShapeTypesEnum.SERVICE) return null;
        const raw = cell.prop('attrs/serviceId/id');
        const n = raw != null ? parseInt(String(raw), 10) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
    })();

    const handleServiceIdChange = (id: number | null) => {
        if (!cell) return;
        cell.prop('attrs/serviceId/id', id ? String(id) : null);
    };

    const chooseInspector = (): ReactElement => {
        switch (cell.get('type')) {
            case ShapeTypesEnum.MESSAGE:
                return <MessageInspector cell={cell as shapes.app.Message} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.LINK:
                return <LinkInspector cell={cell as dia.Link}/>;
            case ShapeTypesEnum.FLOWCHART_START:
                return <LabelInspector cell={cell}/>;
            case ShapeTypesEnum.FLOWCHART_END:
                return <LabelInspector cell={cell}/>;
            case ShapeTypesEnum.RESPONSE_ORIENTATION:
                return <ResponseOrientationInspector cell={cell as shapes.app.ResponseOrientation} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.TALK_ATTENDANT:
                return <TalkAttendantInspector cell={cell as shapes.app.TalkAttendant}/>;
            case ShapeTypesEnum.RESPONSE_PARAM:
                return <ResponseParamInspector cell={cell as shapes.app.ResponseParam} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.SERVICE:
                // Renderizacao especial: tabs (Dados Gerais + Propriedades).
                return renderServiceWithTabs();
            case ShapeTypesEnum.CREATE_TICKET:
                return <CreateTicketInspector cell={cell as shapes.app.CreateTicket} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.AVALIATION:
                return <AvaliationInspector cell={cell as shapes.app.Avaliation} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.FLOW:
                return <FlowInspector cell={cell as shapes.app.Flow} chatbotId={chatbotId} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.LLM:
                return <LLMInspector cell={cell as shapes.app.LLM} chatbotFlowId={chatbotFlowId}/>;
            case ShapeTypesEnum.CONDITION:
                return <ConditionInspector cell={cell as shapes.app.LLM} chatbotFlowId={chatbotFlowId}/>;
            default:
                return;
        }
    };

    const renderServiceWithTabs = (): ReactElement => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <Tabs
                value={serviceTab}
                onChange={(_, v) => setServiceTab(v)}
                variant="fullWidth"
                sx={{ minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.1)', width: '100%' }}
            >
                <Tab label={intl.formatMessage({ id: 'inspector.tab.general', defaultMessage: 'Dados Gerais' })} sx={{ minHeight: 36 }} />
                <Tab label={intl.formatMessage({ id: 'inspector.tab.service', defaultMessage: 'Propriedades do Serviço' })} sx={{ minHeight: 36 }} />
            </Tabs>

            {serviceTab === 0 && (
                // F6 rodada 7: display:flex + flex-direction:column no wrapper.
                // Motivo raiz: os inspectors legados usam `<label>Nome<input/></label>`.
                // O `<label>` HTML nativo eh `display:inline`, entao `input width:100%`
                // pega 100% do inline container = tamanho do texto do label (~"Label" =
                // ~40px). Antes o `.chatbot-inspector` pai era flex-column e os labels
                // eram flex items com align:stretch → esticavam. Ao inserir um Box
                // block, quebrou. Recolocar flex-column faz labels virarem flex items
                // que esticam e voltar a largura historica.
                <Box sx={{
                    pt: 1,
                    overflowY: 'auto',
                    flex: 1,
                    width: '100%',
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <ServiceInspector cell={cell as shapes.app.Service} chatbotFlowId={chatbotFlowId} />
                </Box>
            )}

            {serviceTab === 1 && (
                servicePanelMode === 'docked' ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <ServicePropertiesPanel
                            variant="docked"
                            currentServiceId={currentServiceId}
                            onServiceIdChange={handleServiceIdChange}
                            onToggleFloat={toggleServicePanelMode}
                        />
                    </Box>
                ) : (
                    // Placeholder — panel flutuante ta ativo (renderizado por Chatbot.tsx).
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <p style={{ fontSize: 13 }}>
                            {intl.formatMessage({
                                id: 'inspector.service.floating.notice',
                                defaultMessage: 'As propriedades do serviço estão em painel flutuante. Feche o painel pra voltar aqui.',
                            })}
                        </p>
                        <button
                            className='button-save'
                            onClick={toggleServicePanelMode}
                            style={{ marginTop: 16 }}
                        >
                            {intl.formatMessage({ id: 'inspector.service.dock', defaultMessage: 'Acoplar aqui' })}
                        </button>
                    </Box>
                )
            )}
        </Box>
    );

    const emptyInspector = (): ReactElement => (
        <>
            <h1>Componente Selecionado</h1>
            <label>Label <input disabled/></label>
        </>
    );

    useEffect(() => {
        const [selectedCell = null] = selection as dia.Cell[];
        setCell(selectedCell);
        // Reset da aba ao trocar de cell.
        setServiceTab(0);
    }, [selection]);

    return (
        <>
            <div className={'chatbot-inspector ' + (!cell ? 'disabled-chatbot-inspector' : '')}>
                {cell ? chooseInspector() : emptyInspector()}
                {/* Salvar fluxo sempre visivel — nao interfere nas abas do Service */}
                <button className='button-save' onClick={handleSubmitSaveJson}>Salvar fluxo</button>
            </div>

            {statePage.loading && (
                <Overlay>
                    <CircularProgress color='secondary' />
                </Overlay>
            )}

            <CustomizedSnackbars
                variant={statePage.notificationVariant}
                message={statePage.notificationMessage}
                isOpen={statePage.openNotification}
                toClose={closeNotification}
            />
        </>
    );
};

export default Inspector;
